import * as React from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Search, Send } from "lucide-react";
import { toast } from "sonner";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { getAPIBaseURL, getAdminHeaders } from "@/lib/auth";

type TicketStatus = "open" | "in_progress" | "resolved";

type TicketSummary = {
  id: string;
  subject: string;
  category: string;
  status: TicketStatus;
  created_at: string;
  updated_at: string;
  user_email: string;
  first_name: string | null;
  last_name: string | null;
  message_count: number;
};

type TicketMessage = {
  id: string;
  sender_type: "customer" | "support";
  body: string;
  created_at: string;
};

type TicketDetail = {
  ticket: TicketSummary;
  messages: TicketMessage[];
};

const STATUS_LABELS: Record<TicketStatus, string> = {
  open: "Open",
  in_progress: "In progress",
  resolved: "Resolved",
};

const STATUS_CLASSES: Record<TicketStatus, string> = {
  open: "bg-danger-soft text-danger",
  in_progress: "bg-info-soft text-info",
  resolved: "bg-success-soft text-success",
};

function customerName(ticket: Pick<TicketSummary, "first_name" | "last_name" | "user_email">) {
  const name = [ticket.first_name, ticket.last_name].filter(Boolean).join(" ").trim();
  return name || ticket.user_email;
}

export function SupportPage() {
  const apiBase = getAPIBaseURL();
  const queryClient = useQueryClient();
  const [selectedId, setSelectedId] = React.useState<string | null>(null);
  const [query, setQuery] = React.useState("");
  const [statusFilter, setStatusFilter] = React.useState("all");
  const [reply, setReply] = React.useState("");

  const ticketsQuery = useQuery({
    queryKey: ["support-tickets", statusFilter],
    queryFn: async () => {
      const suffix = statusFilter === "all" ? "" : `?status=${statusFilter}`;
      const response = await fetch(`${apiBase}/api/v1/admin/support/tickets${suffix}`, {
        headers: getAdminHeaders(),
      });
      if (!response.ok) throw new Error("Failed to load tickets");
      const json = await response.json();
      return (json.tickets || []) as TicketSummary[];
    },
    refetchInterval: 60_000,
  });

  const tickets = ticketsQuery.data ?? [];
  const visible = tickets.filter((ticket) => {
    const haystack = `${ticket.id} ${ticket.subject} ${customerName(ticket)} ${ticket.user_email}`.toLowerCase();
    return haystack.includes(query.trim().toLowerCase());
  });

  const activeId = selectedId && visible.some((ticket) => ticket.id === selectedId)
    ? selectedId
    : visible[0]?.id ?? null;

  const detailQuery = useQuery({
    queryKey: ["support-ticket", activeId],
    enabled: Boolean(activeId),
    queryFn: async () => {
      const response = await fetch(`${apiBase}/api/v1/admin/support/tickets/${activeId}`, {
        headers: getAdminHeaders(),
      });
      if (!response.ok) throw new Error("Failed to load ticket");
      return (await response.json()) as TicketDetail;
    },
  });

  const refreshAll = () => {
    queryClient.invalidateQueries({ queryKey: ["support-tickets"] });
    queryClient.invalidateQueries({ queryKey: ["support-ticket", activeId] });
  };

  const sendReply = useMutation({
    mutationFn: async () => {
      const response = await fetch(`${apiBase}/api/v1/admin/support/tickets/${activeId}/messages`, {
        method: "POST",
        headers: getAdminHeaders(),
        body: JSON.stringify({ body: reply.trim() }),
      });
      const json = await response.json().catch(() => ({}));
      if (!response.ok) throw new Error(json.error || "Failed to send reply");
    },
    onSuccess: () => {
      setReply("");
      toast.success("Reply sent");
      refreshAll();
    },
    onError: (error: Error) => toast.error(error.message),
  });

  const changeStatus = useMutation({
    mutationFn: async (status: TicketStatus) => {
      const response = await fetch(`${apiBase}/api/v1/admin/support/tickets/${activeId}`, {
        method: "PATCH",
        headers: getAdminHeaders(),
        body: JSON.stringify({ status }),
      });
      const json = await response.json().catch(() => ({}));
      if (!response.ok) throw new Error(json.error || "Failed to update status");
    },
    onSuccess: () => {
      toast.success("Status updated");
      refreshAll();
    },
    onError: (error: Error) => toast.error(error.message),
  });

  const detail = detailQuery.data;

  return (
    <div className="grid gap-4 lg:grid-cols-[360px_minmax(0,1fr)]">
      <Card className="flex min-h-[640px] flex-col overflow-hidden">
        <div className="space-y-3 border-b p-4">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 size-4 -translate-y-1/2 text-muted-foreground" />
            <Input
              className="pl-9"
              placeholder="Search tickets..."
              aria-label="Search tickets"
              value={query}
              onChange={(event) => setQuery(event.target.value)}
            />
          </div>
          <Select value={statusFilter} onValueChange={setStatusFilter}>
            <SelectTrigger aria-label="Status filter">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All statuses</SelectItem>
              <SelectItem value="open">Open</SelectItem>
              <SelectItem value="in_progress">In progress</SelectItem>
              <SelectItem value="resolved">Resolved</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div className="max-h-[560px] flex-1 overflow-auto">
          {ticketsQuery.isError ? (
            <p className="p-4 text-sm text-destructive">Could not load tickets.</p>
          ) : visible.length === 0 ? (
            <p className="p-4 text-sm text-muted-foreground">No tickets found.</p>
          ) : (
            visible.map((ticket) => (
              <button
                key={ticket.id}
                type="button"
                onClick={() => setSelectedId(ticket.id)}
                className={`grid w-full grid-cols-[auto_minmax(0,1fr)] gap-3 border-b p-4 text-left hover:bg-muted/60 ${activeId === ticket.id ? "bg-primary-soft" : ""}`}
              >
                <Avatar>
                  <AvatarFallback>{customerName(ticket).slice(0, 2).toUpperCase()}</AvatarFallback>
                </Avatar>
                <span className="min-w-0">
                  <span className="flex items-center justify-between gap-2">
                    <strong className="truncate text-sm">{ticket.subject}</strong>
                    <span className={`shrink-0 rounded px-2 py-0.5 text-[10px] font-semibold ${STATUS_CLASSES[ticket.status]}`}>
                      {STATUS_LABELS[ticket.status]}
                    </span>
                  </span>
                  <span className="mt-1 block truncate text-xs text-muted-foreground">
                    {customerName(ticket)} · {ticket.category}
                  </span>
                  <span className="mt-1 block text-[11px] text-muted-foreground">
                    {new Date(ticket.updated_at).toLocaleString()} · {ticket.message_count} message(s)
                  </span>
                </span>
              </button>
            ))
          )}
        </div>
      </Card>

      <Card className="flex min-h-[640px] flex-col overflow-hidden">
        {!activeId || !detail ? (
          <div className="grid flex-1 place-items-center p-8 text-sm text-muted-foreground">
            {detailQuery.isError ? "Could not load this ticket." : activeId ? "Loading…" : "Select a ticket."}
          </div>
        ) : (
          <>
            <div className="flex flex-wrap items-center justify-between gap-3 border-b p-4">
              <div className="min-w-0">
                <h3 className="truncate font-semibold">{detail.ticket.subject}</h3>
                <p className="text-xs text-muted-foreground">
                  {customerName(detail.ticket)} ({detail.ticket.user_email}) · {detail.ticket.category} · #{detail.ticket.id.slice(0, 8).toUpperCase()}
                </p>
              </div>
              <Select
                value={detail.ticket.status}
                onValueChange={(value) => changeStatus.mutate(value as TicketStatus)}
              >
                <SelectTrigger className="w-[160px]" aria-label="Ticket status">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="open">Open</SelectItem>
                  <SelectItem value="in_progress">In progress</SelectItem>
                  <SelectItem value="resolved">Resolved</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="flex-1 space-y-3 overflow-auto p-4">
              {detail.messages.map((message) => (
                <div
                  key={message.id}
                  className={`max-w-[80%] rounded-lg p-3 text-sm ${message.sender_type === "support" ? "ml-auto bg-primary-soft" : "bg-muted"}`}
                >
                  <p className="whitespace-pre-wrap">{message.body}</p>
                  <p className="mt-1 text-[11px] text-muted-foreground">
                    {message.sender_type === "support" ? "Deuxda support" : "Customer"} · {new Date(message.created_at).toLocaleString()}
                  </p>
                </div>
              ))}
            </div>

            <form
              className="grid grid-cols-[minmax(0,1fr)_auto] items-end gap-2 border-t p-4"
              onSubmit={(event) => {
                event.preventDefault();
                if (reply.trim()) sendReply.mutate();
              }}
            >
              <Textarea
                aria-label="Write a reply"
                placeholder="Write a reply..."
                className="min-h-10 resize-none"
                value={reply}
                maxLength={5000}
                onChange={(event) => setReply(event.target.value)}
              />
              <Button type="submit" size="icon" aria-label="Send reply" disabled={!reply.trim() || sendReply.isPending}>
                <Send />
              </Button>
            </form>
          </>
        )}
      </Card>
    </div>
  );
}
